import type { ArcadeScreen } from "../ScreenManager.js";
import type { MenuAction } from "../MenuInput.js";
import { uiSfx } from "../uiSfx.js";
import type { LobbyFlow } from "./LobbyFlow.js";
import type { HostedMatchType, LobbySettingsPayload } from "../../networking/ClientNetwork.js";
import {
  MATCH_TYPE_OPTIONS,
  MAX_PLAYERS_OPTIONS,
  ROUND_SECONDS_OPTIONS,
  SCORE_LIMIT_OPTIONS,
  cycleOption,
  escapeHtml,
  formatClock,
  headerFor,
  matchTypeLabel,
  remainingMs,
  renderLobbyRoster,
  renderLobbyStandings,
  roundEndLine,
  settingsSummary,
  startRejectedLine,
  statusLineFor,
  winnerNameOf,
} from "../lobbyState.js";

/**
 * LOBBY ROOM — roster / ready-up / leader settings / post-round standings
 * (menu-redesign-plan §3.5, wire contract in HostedArenaRoom.ts).
 *
 * A thin projection of LobbyFlow's machine. Four render states share the
 * screen: CONNECTING, LOBBY (code + roster + controls), POST (standings +
 * rematch) and ERROR. During a live round the shell is hidden entirely —
 * LobbyFlow re-shows this screen when `roundEnd` lands.
 *
 * Focus model: every actionable element carries `data-focus`; after each
 * render the visible ones are collected in DOM order and up/down walk them.
 * Settings rows adjust with left/right (leader only); everything is equally
 * clickable for mouse/touch.
 */

export interface LobbyRoomDeps {
  flow: LobbyFlow;
}

const TICK_MS = 250;
const TOAST_MS = 4000;

interface SettingRow {
  key: keyof LobbySettingsPayload;
  label: string;
  value: string;
  adjust: ((dir: 1 | -1) => void) | null;
}

export class LobbyRoomScreen implements ArcadeScreen {
  private root: HTMLElement | null = null;
  private active = false;
  private focusIdx = 0;
  private tick: ReturnType<typeof setInterval> | null = null;
  private toastShownAt = 0;
  private lastToast = "";
  private focusables: HTMLElement[] = [];

  constructor(private readonly deps: LobbyRoomDeps) {
    // Model changes re-render whenever this screen is on stage.
    deps.flow.onChange(() => { if (this.active) this.render(); });
  }

  mount(root: HTMLElement): void {
    this.root = root;
    root.innerHTML = `
      <div class="lb-wrap lb-room">
        <div class="lb-headline">
          <span class="lb-eyebrow" data-el="header">HOSTED LOBBY</span>
          <span class="lb-headrule" aria-hidden="true"></span>
          <span class="lb-head-mini" data-el="mini"></span>
        </div>
        <div class="lb-toast hidden" data-el="toast" role="status"></div>

        <div class="lb-state" data-state="connecting">
          <div class="lb-connecting-big">···</div>
          <div class="lb-statusline" data-el="connmsg">LINKING…</div>
          <div class="lb-btnrow">
            <button type="button" class="lb-btn" data-focus data-act="cancel">CANCEL</button>
          </div>
        </div>

        <div class="lb-state" data-state="lobby">
          <div class="lb-codebar">
            <span class="lb-codebar-label">ROOM CODE</span>
            <span class="lb-code-big" data-el="code">·····</span>
            <span class="lb-codebar-share">FRIENDS JOIN VIA LOBBIES ▸ ENTER CODE</span>
          </div>
          <div class="lb-roster" data-el="roster"></div>
          <div class="lb-statusline" data-el="status"></div>
          <div class="lb-reject hidden" data-el="reject" role="alert"></div>
          <div class="lb-controls">
            <button type="button" class="lb-btn lb-btn-ready" data-focus data-act="ready">READY UP</button>
            <div class="lb-settings" data-el="settings"></div>
            <div class="lb-btnrow">
              <button type="button" class="lb-btn lb-btn-start" data-focus data-act="start">START MATCH</button>
              <button type="button" class="lb-btn lb-btn-force hidden" data-focus data-act="force">FORCE START</button>
              <button type="button" class="lb-btn lb-btn-leave" data-focus data-act="leave">LEAVE ROOM</button>
            </div>
          </div>
        </div>

        <div class="lb-state" data-state="post">
          <div class="lb-post-reason" data-el="reason"></div>
          <div class="lb-winner hidden" data-el="winner"></div>
          <div class="lb-standings" data-el="standings"></div>
          <div class="lb-statusline" data-el="poststatus"></div>
          <div class="lb-post-timer" data-el="posttimer"></div>
          <div class="lb-btnrow">
            <button type="button" class="lb-btn lb-btn-start" data-focus data-act="rematch">REMATCH</button>
            <button type="button" class="lb-btn" data-focus data-act="tolobby">BACK TO LOBBY</button>
            <button type="button" class="lb-btn lb-btn-leave" data-focus data-act="leave">LEAVE</button>
          </div>
        </div>

        <div class="lb-state" data-state="error">
          <div class="lb-error-big">!</div>
          <div class="lb-statusline" data-el="errmsg"></div>
          <div class="lb-btnrow">
            <button type="button" class="lb-btn" data-focus data-act="backerr">BACK TO LOBBIES</button>
          </div>
        </div>

        <div class="lb-footer">
          <span class="lb-hints">↑↓ SELECT &nbsp;·&nbsp; ◂ ▸ ADJUST &nbsp;·&nbsp; ENTER / Ⓐ CONFIRM &nbsp;·&nbsp; ESC / Ⓑ LEAVE</span>
        </div>
      </div>`;

    root.addEventListener("click", (ev) => {
      const target = (ev.target as HTMLElement).closest<HTMLElement>("[data-act]");
      if (!target) return;
      this.runAction(target.dataset["act"]!, target);
    });
    root.addEventListener("mouseover", (ev) => {
      const target = (ev.target as HTMLElement).closest<HTMLElement>("[data-focus]");
      if (!target) return;
      const idx = this.focusables.indexOf(target);
      if (idx >= 0 && idx !== this.focusIdx) { this.focusIdx = idx; this.applyFocus(); }
    });
  }

  onEnter(): void {
    this.active = true;
    this.focusIdx = 0;
    this.startTick();
    this.render();
  }

  onLeave(): void {
    this.active = false;
    this.stopTick();
  }

  handleInput(action: MenuAction): boolean {
    const phase = this.deps.flow.machine.model().phase;
    switch (action.type) {
      case "up":
        this.moveFocus(-1);
        return true;
      case "down":
        this.moveFocus(1);
        return true;
      case "left":
      case "right": {
        const el = this.focusables[this.focusIdx];
        if (el?.dataset["key"]) {
          this.adjustSetting(el.dataset["key"]!, action.type === "right" ? 1 : -1);
          return true;
        }
        this.moveFocus(action.type === "right" ? 1 : -1);
        return true;
      }
      case "confirm":
      case "start": {
        const el = this.focusables[this.focusIdx];
        if (!el) return true;
        if (el.dataset["key"]) this.adjustSetting(el.dataset["key"]!, 1);
        else this.runAction(el.dataset["act"] ?? "", el);
        return true;
      }
      case "back": {
        if (phase === "connecting") { this.runAction("cancel", null); return true; }
        if (phase === "error" || phase === "rejected") { this.runAction("backerr", null); return true; }
        // First B/Esc walks to LEAVE; a second one activates it. No accidental quits.
        const leaveIdx = this.focusables.findIndex((el) => el.dataset["act"] === "leave");
        if (leaveIdx === -1) return true;
        if (this.focusIdx === leaveIdx) {
          this.runAction("leave", null);
        } else {
          uiSfx.play("nav");
          this.focusIdx = leaveIdx;
          this.applyFocus();
        }
        return true;
      }
      default:
        return false;
    }
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  private runAction(act: string, el: HTMLElement | null): void {
    const flow = this.deps.flow;
    switch (act) {
      case "ready":   uiSfx.play("confirm"); flow.toggleReady(); break;
      case "start":   uiSfx.play("confirm"); flow.start(false); break;
      case "force":   uiSfx.play("confirm"); flow.start(true); break;
      case "rematch": uiSfx.play("confirm"); flow.rematch(); break;
      case "tolobby": uiSfx.play("confirm"); flow.backToLobbyEarly(); break;
      case "leave":   uiSfx.play("back");    flow.leaveRoom(); break;
      case "cancel":  uiSfx.play("back");    flow.cancelConnect(); break;
      case "backerr": uiSfx.play("back");    flow.leaveRoom(); break;
      case "adj": {
        // ◂ / ▸ arrows inside a settings row (mouse path).
        const row = el?.closest<HTMLElement>("[data-key]");
        if (row) this.adjustSetting(row.dataset["key"]!, el?.dataset["dir"] === "-1" ? -1 : 1);
        break;
      }
      default: break;
    }
  }

  private adjustSetting(key: string, dir: 1 | -1): void {
    const flow = this.deps.flow;
    const m = flow.machine.model();
    if (!m.isLeader || m.phase !== "lobby") { uiSfx.play("deny"); return; }
    const s = flow.effectiveSettings();
    if (!s) return;
    uiSfx.play("nav");
    switch (key) {
      case "matchType":
        flow.changeSetting({ matchType: cycleOption<HostedMatchType>(MATCH_TYPE_OPTIONS, s.matchType, dir) });
        break;
      case "roundSeconds":
        flow.changeSetting({ roundSeconds: cycleOption(ROUND_SECONDS_OPTIONS, s.roundSeconds, dir) });
        break;
      case "scoreLimit":
        flow.changeSetting({ scoreLimit: cycleOption(SCORE_LIMIT_OPTIONS, s.scoreLimit, dir) });
        break;
      case "botFill":
        flow.changeSetting({ botFill: !s.botFill });
        break;
      case "maxPlayers":
        flow.changeSetting({ maxPlayers: cycleOption(MAX_PLAYERS_OPTIONS, s.maxPlayers, dir) });
        break;
      default:
        break;
    }
  }

  // ── Focus ─────────────────────────────────────────────────────────────────

  private moveFocus(delta: number): void {
    if (this.focusables.length === 0) return;
    this.focusIdx = (this.focusIdx + delta + this.focusables.length) % this.focusables.length;
    uiSfx.play("nav");
    this.applyFocus();
  }

  private collectFocusables(): void {
    if (!this.root) return;
    const all = [...this.root.querySelectorAll<HTMLElement>("[data-focus]")];
    this.focusables = all.filter((el) => {
      if (el.classList.contains("hidden")) return false;
      const state = el.closest<HTMLElement>("[data-state]");
      return !state || !state.classList.contains("hidden");
    });
    if (this.focusIdx >= this.focusables.length) this.focusIdx = 0;
  }

  private applyFocus(): void {
    this.focusables.forEach((el, i) => el.classList.toggle("is-focused", i === this.focusIdx));
  }

  // ── Rendering ─────────────────────────────────────────────────────────────

  private startTick(): void {
    if (this.tick !== null) return;
    this.tick = setInterval(() => this.render(), TICK_MS);
  }

  private stopTick(): void {
    if (this.tick !== null) { clearInterval(this.tick); this.tick = null; }
  }

  private el(name: string): HTMLElement | null {
    return this.root?.querySelector(`[data-el="${name}"]`) ?? null;
  }

  private setState(state: "connecting" | "lobby" | "post" | "error"): void {
    this.root?.querySelectorAll<HTMLElement>("[data-state]").forEach((el) => {
      el.classList.toggle("hidden", el.dataset["state"] !== state);
    });
  }

  private render(): void {
    if (!this.root) return;
    const flow = this.deps.flow;
    const m = flow.machine.model();
    const now = Date.now();

    const headerEl = this.el("header");
    if (headerEl) headerEl.textContent = headerFor(m);

    // Leader-migration toast, screen-managed 4 s lifetime.
    const toastEl = this.el("toast");
    if (toastEl) {
      if (m.leaderNotice && m.leaderNotice !== this.lastToast) {
        this.lastToast = m.leaderNotice;
        this.toastShownAt = now;
      }
      if (m.leaderNotice && now - this.toastShownAt >= TOAST_MS) {
        flow.machine.clearLeaderNotice();
        this.lastToast = "";
      }
      const model = flow.machine.model();
      toastEl.textContent = model.leaderNotice;
      toastEl.classList.toggle("hidden", !model.leaderNotice);
    }

    switch (m.phase) {
      case "idle":
      case "connecting":
      case "match": { // brief flash between `start` and the shell hiding
        this.setState("connecting");
        const msg = this.el("connmsg");
        if (msg) msg.textContent = m.phase === "match" ? "DROPPING IN…" : statusLineFor(m);
        break;
      }
      case "error":
      case "rejected": {
        this.setState("error");
        const msg = this.el("errmsg");
        if (msg) msg.textContent = m.errorMessage;
        break;
      }
      case "post": {
        this.setState("post");
        const reason = this.el("reason");
        if (reason) reason.textContent = roundEndLine(m.roundEndReason);
        const winner = winnerNameOf(m.standings);
        const winnerEl = this.el("winner");
        if (winnerEl) {
          winnerEl.classList.toggle("hidden", !winner);
          if (winner) winnerEl.innerHTML = `WINNER — ${escapeHtml(winner.toUpperCase())}`;
        }
        const standingsEl = this.el("standings");
        if (standingsEl) standingsEl.innerHTML = renderLobbyStandings(m.standings, flow.ownId());
        const timerEl = this.el("posttimer");
        if (timerEl) {
          const left = remainingMs(m.postEndsAt, now);
          timerEl.textContent = left > 0 ? `BACK TO LOBBY IN ${formatClock(left)}` : "";
        }
        this.root.querySelector<HTMLElement>('[data-act="rematch"]')?.classList.toggle("hidden", !m.isLeader);
        this.root.querySelector<HTMLElement>('[data-act="tolobby"]')?.classList.toggle("hidden", !m.isLeader);
        const postStatus = this.el("poststatus");
        if (postStatus) postStatus.textContent = statusLineFor(m);
        break;
      }
      case "lobby": {
        this.setState("lobby");
        const codeEl = this.el("code");
        if (codeEl) codeEl.textContent = m.code ?? "·····";
        const settings = flow.effectiveSettings();
        const rosterEl = this.el("roster");
        if (rosterEl) {
          rosterEl.innerHTML = renderLobbyRoster(m.members, flow.ownId(), settings?.maxPlayers ?? 8);
        }
        const statusEl = this.el("status");
        if (statusEl) statusEl.textContent = statusLineFor(m);

        const rejectEl = this.el("reject");
        if (rejectEl) {
          rejectEl.classList.toggle("hidden", !m.startRejected);
          if (m.startRejected) rejectEl.textContent = startRejectedLine(m.startRejected);
        }

        const readyBtn = this.root.querySelector<HTMLElement>('[data-act="ready"]');
        if (readyBtn) {
          const ready = flow.machine.selfReady();
          readyBtn.textContent = ready ? "READY ✓ — TAP TO UNREADY" : "READY UP";
          readyBtn.classList.toggle("is-ready", ready);
        }

        const startBtn = this.root.querySelector<HTMLElement>('[data-act="start"]');
        if (startBtn) startBtn.classList.toggle("hidden", !m.isLeader);
        const forceBtn = this.root.querySelector<HTMLElement>('[data-act="force"]');
        if (forceBtn) {
          forceBtn.classList.toggle("hidden", !(m.isLeader && m.startRejected?.reason === "not-ready"));
        }

        this.renderSettings(settings, m.isLeader);
        break;
      }
    }

    const mini = this.el("mini");
    if (mini) {
      mini.textContent =
        m.phase === "lobby" && m.settings
          ? `${m.members.length} / ${flow.effectiveSettings()?.maxPlayers ?? 8} DRIVERS`
          : "";
    }

    this.collectFocusables();
    this.applyFocus();
  }

  private renderSettings(s: LobbySettingsPayload | null, isLeader: boolean): void {
    const host = this.el("settings");
    if (!host) return;
    if (!s) { host.innerHTML = ""; return; }
    if (!isLeader) {
      host.innerHTML = `<div class="lb-settings-ro">${escapeHtml(settingsSummary(s))}</div>`;
      return;
    }
    const rows: SettingRow[] = [
      { key: "matchType", label: "MATCH TYPE", value: matchTypeLabel(s.matchType), adjust: null },
      { key: "roundSeconds", label: "ROUND TIME", value: formatClock(s.roundSeconds * 1000), adjust: null },
      { key: "scoreLimit", label: "SCORE LIMIT", value: s.scoreLimit > 0 ? `FIRST TO ${s.scoreLimit}` : "TIMER ONLY", adjust: null },
      { key: "botFill", label: "BOT FILL", value: s.botFill ? "ON" : "OFF", adjust: null },
      { key: "maxPlayers", label: "MAX PLAYERS", value: String(s.maxPlayers), adjust: null },
    ];
    const dimScore = s.matchType !== "deathmatch";
    host.innerHTML = rows.map((row) => {
      const dim = row.key === "scoreLimit" && dimScore ? " dim" : "";
      return `<div class="lb-setting${dim}" data-focus data-key="${row.key}">` +
        `<span class="lb-setting-label">${row.label}</span>` +
        `<button type="button" class="lb-adj" data-act="adj" data-dir="-1" tabindex="-1">◂</button>` +
        `<span class="lb-setting-value">${escapeHtml(row.value)}</span>` +
        `<button type="button" class="lb-adj" data-act="adj" data-dir="1" tabindex="-1">▸</button>` +
        `</div>`;
    }).join("");
  }
}
